import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  CardContent,
  TextField,
  Button,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
  Card,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { canShowPermission } from "../Permissions/permissionObject";

const BASE_API = `${process.env.REACT_APP_API_BASE_URL}/api/v1/customer-interaction`;

const CustomerInteraction = () => {
  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Failed to parse userPermissions", e);
      }
    }
  }, []);

  const canCreate = canShowPermission(permissions, "customerInteraction", "create");
  const canEdit = canShowPermission(permissions, "customerInteraction", "edit");
  const canDelete = canShowPermission(permissions, "customerInteraction", "delete");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [files, setFiles] = useState([]); // holds raw files
  const [previews, setPreviews] = useState([]); // { type: image/video, src: preview url }

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch interactions
  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const res = await axios.get(`${BASE_API}/list`, {
        headers: { Authorization: token },
      });

      setList(Array.isArray(res.data?.data) ? res.data.data : []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Select multiple images/videos
  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);

    // add files in array
    setFiles((prev) => [...prev, ...selected]);

    // create preview for images/videos
    const newPreviews = selected.map((file) => {
      if (file.type.startsWith("image/")) {
        return { type: "image", src: URL.createObjectURL(file) };
      }
      if (file.type.startsWith("video/")) {
        return { type: "video", src: URL.createObjectURL(file) };
      }
      return null;
    });

    setPreviews((prev) => [...prev, ...newPreviews]);
  };

  // Remove file by index
  const handleRemoveImage = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this interaction?"))
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${BASE_API}/soft-delete/${id}`,
        {},
        { headers: { Authorization: token } }
      );

      setList((prev) => prev.filter((item) => item._id !== id));
    } catch (err) {
      alert("Delete failed!");
      console.error(err);
    }
  };

  // Submit form
  const uploadInteraction = async (e) => {
    e.preventDefault();

    const form = new FormData();
    form.append("title", title);
    form.append("description", description);

    files.forEach((file) => form.append("files", file));

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      await axios.post(`${BASE_API}/create`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token,
        },
      });

      alert("Uploaded successfully!");

      setTitle("");
      setDescription("");
      setFiles([]);
      setPreviews([]);

      fetchData();
    } catch (err) {
      alert("Upload Failed!");
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", p: 2, mt: 10 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Customer Interaction
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Upload Form - only when create permission */}
      {canCreate && (
      <Box
        sx={{
          width: "100%",
          mb: 3,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          fontWeight={700}
          fontSize={24}
          p={2}
          sx={{ borderBottom: "1px solid #eee" }}
        >
          Upload Customer Interaction
        </Typography>

        <CardContent>
          <form onSubmit={uploadInteraction}>
            <Grid container spacing={4}>
              {/* LEFT COLUMN */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={3} direction="column">
                  <Grid item>
                    <TextField
                      fullWidth
                      label="Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                  </Grid>

                  <Grid item>
                    <TextField
                      fullWidth
                      label="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                      multiline
                      rows={3}
                    />
                  </Grid>

                  <Grid item>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                      startIcon={<CloudUploadIcon />}
                    >
                      {files.length > 0
                        ? `${files.length} files selected`
                        : "Upload Images / Videos"}
                      <input
                        hidden
                        multiple
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleFileSelect}
                      />
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

              {/* RIGHT COLUMN (PREVIEWS) */}
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  {previews.map((item, index) => (
                    <Grid item key={index}>
                      <Box sx={{ position: "relative" }}>
                        {item.type === "image" ? (
                          <img
                            src={item.src}
                            alt="preview"
                            style={{
                              width: 140,
                              height: 160,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #ccc",
                            }}
                          />
                        ) : (
                          <video
                            src={item.src}
                            controls
                            style={{
                              width: 140,
                              height: 160,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #ccc",
                            }}
                          />
                        )}

                        <IconButton
                          onClick={() => handleRemoveImage(index)}
                          sx={{
                            position: "absolute",
                            top: -10,
                            right: -10,
                            background: "#f44336",
                            color: "white",
                            "&:hover": { background: "#d32f2f" },
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* SUBMIT BUTTON */}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ height: 48 }}
                >
                  {loading ? <CircularProgress size={24} /> : "Upload Interaction"}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Box>
      )}

      {/* LIST OF INTERACTIONS */}
      <Box
        sx={{
          width: "100%",
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          fontWeight={700}
          fontSize={24}
          p={2}
          sx={{ borderBottom: "1px solid #eee" }}
        >
          Previous Interactions ({list.length})
        </Typography>

        <Grid container spacing={2} sx={{ p: 2 }}>
          {list.map((item) => (
            <Grid item xs={12} md={6} key={item._id}>
              <Card
                sx={{
                  p: 2,
                  position: "relative",
                  borderRadius: 3,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
                  },
                }}
              >
                {canDelete && (
                <IconButton
                  onClick={() => handleDelete(item._id)}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    color: "white",
                    backgroundColor: "rgba(255,0,0,0.7)",
                    "&:hover": { backgroundColor: "rgba(255,0,0,0.9)" },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
                )}

                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {item.title}
                </Typography>
                <Typography sx={{ mb: 2, color: "text.secondary" }}>
                  {item.description}
                </Typography>

                {/* MEDIA GRID */}
                {Array.isArray(item.urls) && item.urls.length > 0 && (
                  <Grid container spacing={1}>
                    {item.urls.map((media, idx) => (
                      <Grid item xs={6} sm={4} key={idx}>
                        <Box sx={{ position: "relative" }}>
                          {media.mediaType?.includes("video") ? (
                            <video
                              src={media.url}
                              controls
                              style={{
                                width: "100%",
                                height: 140,
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                          ) : (
                            <img
                              src={media.url}
                              alt={`media-${idx}`}
                              style={{
                                width: "100%",
                                height: 140,
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}

                <Typography
                  variant="caption"
                  color="gray"
                  sx={{ mt: 1, display: "block" }}
                >
                  {new Date(item.createdAt).toLocaleString("en-IN")}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default CustomerInteraction;
